<template>
  <div id="users">
    <b-card no-body header-tag="header" footer-tag="footer">
      <template slot="header">
        <div class="total-count mr-3">
          {{ totalRows }}
          <span class="label">Users found</span>
        </div>
        <font-awesome-icon icon="filter" fixed-width class="icon"/>
        <b-form-group class="mb-0">
          <b-form-radio-group v-model="showDeactivated">
            <b-form-radio
              @change="toggleStatusFilter()"
              v-model="showDeactivated"
              :value="false"
              class="ml-3"
            >Active Users</b-form-radio>
            <b-form-radio
              @change="toggleStatusFilter()"
              v-model="showDeactivated"
              :value="true"
            >Deactivated Users</b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <!-- Search Box -->
        <div class="search-box" :class="{active: filter}">
          <div class="input-group">
            <div class="input-group-prepend">
              <div class="input-group-text">
                <font-awesome-icon icon="search" fixed-width class="icon"/>
              </div>
            </div>
            <input
              type="text"
              class="form-control"
              v-model="filter"
              @keyup="searchUsers()"
              placeholder="Find a user by name or email..."
            >
            <button class="clear-search" @click="clearSearch()">
              <font-awesome-icon icon="times-circle" fixed-width class="icon"/>
            </button>
          </div>
        </div>
      </template>
      <b-card-body>
        <div class="d-flex align-items-center justify-content-between">
          <!-- Per page selection -->
          <div class="form-inline per-page-selection d-flex align-items-center float-left">
            <b-form-select :options="pageOptions" v-model="perPage" @change="currentPage=1"/>
            <span class="ml-2 per-page-label">Users Per Page</span>
          </div>
          <!-- Add User Button -->
          <button class="btn btn-primary float-right" @click="addUser()">
            <font-awesome-icon icon="plus" class="icon"/>Add User
          </button>
        </div>
      </b-card-body>

      <!--
        Data Table
      -->
      <b-table
        ref="usersTable"
        show-empty
        striped
        responsive
        :busy.sync="loading"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :items="fetchUsers"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        @filtered="onFiltered"
        empty-text="There are no users to display"
      >
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle" variant="primary"/>
        </div>
        <!-- Name -->
        <template slot="name" slot-scope="data">
          <strong>{{ data.item.name }}</strong>
          <span class="badge badge-success float-right ml-1" v-if="data.item.useApi">API</span>
          <span class="badge badge-secondary float-right" v-if="data.item.roleId === 1">Admin</span>
        </template>

        <!-- Status -->
        <template slot="status" slot-scope="data">
          <span
            class="badge badge-md"
            :class="'badge-' + getUserStatus(data.item.isDeleted)"
          >{{ getUserStatus(data.item.isDeleted) }}</span>
        </template>

        <!-- Clients -->
        <template slot="clients" slot-scope="data">
          <ul v-if="data.item.clients.length" class="user-clients list-unstyled list-inline mb-0">
            <li
              class="list-inline-item mr-1"
              v-for="client in data.item.clients"
              :key="client.id"
            >{{ client.name }}</li>
          </ul>
        </template>

        <!-- Actions -->
        <template slot="actions" slot-scope="data">
          <div class="float-right" v-if="!data.item.isDeleted">
            <b-dropdown right no-flip variant="link" no-caret>
              <template slot="button-content">
                <font-awesome-icon icon="ellipsis-h"/>
                <span class="sr-only">Edit User</span>
              </template>

              <b-dropdown-item href="#" @click="editUser(data.item)">
                <font-awesome-icon icon="pencil-alt" fixed-width class="mr-2"/>Edit User
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="resendEmail(data.item.id)">
                <font-awesome-icon icon="envelope" fixed-width class="mr-2"/>Resend Email
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="resetPassword(data.item.id)">
                <font-awesome-icon icon="user-lock" fixed-width class="mr-2"/>Log Out and Reset Password
              </b-dropdown-item>
              <b-dropdown-divider v-if="authUser.id != data.item.id"/>
              <b-dropdown-item
                href="#"
                class="text-danger"
                v-if="authUser.id != data.item.id"
                @click="deleteUser(data.item.id, data.item.name)"
              >
                <font-awesome-icon icon="ban" fixed-width class="mr-2"/>Deactivate User
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>

      <!-- Footer -->
      <template slot="footer">
        <div class="d-flex align-items-center justify-content-between">
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage"/>
        </div>
      </template>
    </b-card>

    <!--
      User Modal
    -->
    <b-modal
      ref="userModal"
      size="lg"
      centered
      hide-header-close
      :title="isEditing ? 'Edit User' : 'Add New User'"
    >
      <form autocomplete="off">
        <div class="form-row">
          <div class="col">
            <div class="form-group">
              <label>Name</label>
              <span class="required">*</span>
              <input
                type="text"
                v-model="user.name"
                class="form-control"
                :class="{'is-invalid': validationErrors.name}"
                autocomplete="off"
                required="true"
                @blur="pristineName = false"
              >
              <p v-if="validationErrors.name" class="invalid-feedback">{{ validationErrors.name }}</p>
            </div>
          </div>
          <div class="col">
            <div class="form-group">
              <label>Email Address</label>
              <span class="required">*</span>
              <input
                type="email"
                v-model="user.email"
                class="form-control"
                :class="{'is-invalid': validationErrors.email}"
                autocomplete="off"
                required="true"
                :disabled="isEditing"
                @blur="pristineEmail = false"
              >
              <p v-if="validationErrors.email" class="invalid-feedback">{{ validationErrors.email }}</p>
            </div>
          </div>
        </div>
        <div class="form-row">
          <div class="col">
            <div class="form-group">
              <label>User Role</label>
              <div>
                <b-form-select :options="roleOptions" v-model="user.roleId"/>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="form-group">
              <label>API Access</label>
              <div>
                <b-form-checkbox v-model="user.useApi">Allow API access</b-form-checkbox>
              </div>
            </div>
          </div>
        </div>
        <hr>
        <!-- Client Search -->
        <h4 class="mb-3">Add clients</h4>
        <div class="client-search">
          <div class="search-input">
            <font-awesome-icon icon="search" class="icon"/>
            <input
              type="text"
              class="form-control"
              placeholder="Find a client by name..."
              @input="onSearch"
              v-model="searchClients"
            >
            <font-awesome-icon
              icon="times-circle"
              class="clear-search"
              @click="clearClientSearch()"
              v-if="searchClients != ''"
            />
          </div>
          <div class="search-results" v-show="isOpen">
            <div v-if="!filteredClients.length">No clients found</div>
            <div
              class="client-item add-client"
              :class="{selected: inArray(client, user.clients)}"
              v-for="client in filteredClients"
              :key="'a_' + client.id"
              @click="selectClient(client)"
            >
              <p>
                {{ client.name }}
                <span class="text-secondary smallclientidtext ml-1">{{ client.id }}</span>
              </p>
              <font-awesome-icon icon="check-circle" class="icon"/>
            </div>
          </div>
        </div>

        <div class="selected-clients">
          <h4>Selected Clients</h4>
          <p
            v-if="!user.clients.length"
            class="mb-0 empty-state-text"
          >Must select at least one client</p>
          <div
            class="client-item remove-client"
            v-for="client in user.clients"
            :key="'b_' + client.id"
            @click="removeClient(client)"
          >
            <p>
              {{ client.name }}
              <span class="text-secondary smallclientidtext ml-1">{{ client.id }}</span>
            </p>
            <font-awesome-icon icon="times" class="icon"/>
          </div>
        </div>
      </form>
      <template slot="modal-footer">
        <button
          class="btn btn-primary"
          @click="saveUser()"
          v-if="!isEditing"
          :disabled="invalidForm || isSubmitting"
        >
          <span v-if="!isSubmitting">Add User</span>
          <font-awesome-icon icon="spinner" spin class="mr-2" v-if="isSubmitting"/>
          <span v-if="isSubmitting">Adding User...</span>
        </button>
        <button
          class="btn btn-primary"
          @click="updateUser()"
          v-if="isEditing"
          :disabled="invalidForm || isSubmitting"
        >
          <span v-if="!isSubmitting">Update User</span>
          <font-awesome-icon icon="spinner" spin class="mr-2" v-if="isSubmitting"/>
          <span v-if="isSubmitting">Updating User...</span>
        </button>
        <button class="btn btn-outline-secondary" @click="hideModal()">Cancel</button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import router from "@/router";
import _ from "lodash";
import PortalSidebar from "@/components/PortalSidebar.vue";
import PortalPageHeader from "@/components/PortalPageHeader.vue";
import DropdownFilter from "@/components/DropdownFilter.vue";
import SelectedFilters from "@/components/SelectedFilters.vue";
import { EventBus } from "@/services/event-bus.js";
import { getCurrentUser, hasAdminRole } from "@/services/usersession";
import {
  searchUsers,
  getUsers,
  addUser,
  updateUser,
  deleteUser,
  getAllClients
} from "@/services/api";

export default {
  name: "Users",
  components: {
    PortalSidebar,
    PortalPageHeader,
    DropdownFilter,
    SelectedFilters
  },
  data() {
    return {
      authUser: null,
      fields: [
        {
          key: "status"
        },
        {
          key: "name",
          sortable: true
        },
        {
          key: "email",
          sortable: true
        },
        {
          key: "clients"
        },
        { key: "actions", label: "" }
      ],
      loading: false,
      currentPage: 1,
      perPage: 20,
      totalRows: null,
      sortBy: "name",
      sortDesc: false,
      pageOptions: [20, 50, 100, 500],
      filter: "",
      searchClients: "",
      isOpen: false,
      clients: [],
      filteredClients: [],
      user: {
        name: "",
        email: "",
        clients: [],
        useApi: false,
        roleId: 2
      },
      roleOptions: [{ value: 1, text: "Admin" }, { value: 2, text: "User" }],
      validationErrors: {},
      isEditing: false,
      isSubmitting: false,
      timeout: null,
      showDeactivated: false,
      pristineEmail: true,
      pristineName: true
    };
  },
  beforeCreate() {
    if (!hasAdminRole(this.$session)) {
      router.push("/");
    }
  },
  mounted() {
    this.fetchClients();
    this.authUser = getCurrentUser(this.$session);
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalRows / this.perPage);
    },
    invalidForm() {
      // errors from failed saving/updating need to be retained
      if (!this.validationErrors.dontclear) {
        this.validationErrors = {};
      }

      if (!this.user || !this.user.clients) {
        return true;
      }

      let hasError = false;
      if (
        !this.user.email ||
        (this.user.email && this.user.email.trim() === "")
      ) {
        if (!this.pristineEmail) {
          this.validationErrors.email = "Required field";
        }
        hasError = true;
      }

      // must have username and email address
      if (!this.user.name || (this.user.name && this.user.name.trim() === "")) {
        if (!this.pristineName) {
          this.validationErrors.name = "Required field";
        }
        hasError = true;
      }

      // must have at least one client
      if (this.user.clients && this.user.clients.length < 1) {
        hasError = true;
      }

      // validate the email field - manually
      const pattern = new RegExp(
        "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$"
      );
      const result = pattern.test(this.user.email);
      if (!this.validationErrors.email && !result) {
        if (!this.pristineEmail) {
          this.validationErrors.email = "Invalid email address";
        }
        hasError = true;
      }

      // all checks pass
      return hasError;
    }
  },
  watch: {
    loading: function() {
      if (this.loading) {
        EventBus.$emit("showLoader");
      } else {
        EventBus.$emit("hideLoader");
      }
    }
  },
  methods: {
    /** -----------------
     *
     *   User List Display
     *
     * ----------------- */

    async fetchUsers(ctx) {
      const params = {
        filterCriteria: [],
        sortBy: ctx.sortBy == null ? "name" : ctx.sortBy,
        sortDirection: ctx.sortDesc ? "desc" : "asc",
        page: this.currentPage == 1 ? this.currentPage : ctx.currentPage,
        pageSize: ctx.perPage
      };

      params.filterCriteria.push({
        property: "isDeleted",
        criteria: this.showDeactivated
      });

      if (this.filter && this.filter.trim() !== "") {
        params.filterCriteria.push({
          property: "keyword",
          criteria: this.filter
        });
      }

      const result = await searchUsers(params);
      this.totalRows = result.totalResults;
      this.currentPage = ctx.currentPage;

      return result.results;
    },
    searchUsers() {
      this.currentPage = 1;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (this.filter.length < 1) {
          this.clearSearch();
        }
        this.refreshTable();
      }, 500);
    },
    clearSearch() {
      this.filter = "";
      this.refreshTable();
    },
    toggleStatusFilter() {
      this.currentPage = 1;
      this.showDeactivated = !this.showDeactivated;
      this.refreshTable();
    },
    getUserStatus(isDeleted) {
      return isDeleted ? "deactivated" : "active";
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    refreshTable() {
      this.$refs.usersTable.refresh();
    },
    /** -----------------
     *
     *   User CRUD
     *
     * ----------------- */

    /*
      Add/Remove clients
    */
    async fetchClients() {
      const result = await getAllClients();
      this.clients = result;
    },
    onSearch() {
      this.filterClients();
      if (this.searchClients != "") {
        this.isOpen = true;
      } else {
        this.isOpen = false;
        this.filteredClients = [];
      }
    },
    clearClientSearch() {
      this.filteredClients = [];
      this.searchClients = "";
      this.isOpen = false;
    },
    filterClients() {
      const limit = 50;
      this.filteredClients = this.clients.filter(item => {
        return item.name
          .toLowerCase()
          .includes(this.searchClients.toLowerCase());
      });
      this.filteredClients = this.filteredClients.slice(0, limit);
    },
    selectClient(client) {
      const index = this.user.clients.findIndex(e => e.id === client.id);
      // Only add if client doesnt already exist in array
      if (index === -1) {
        this.user.clients.push(client);
      }
      this.searchClients = "";
      this.filteredClients = [];
      this.isOpen = false;
    },
    removeClient(client) {
      this.user.clients = _.remove(this.user.clients, el => {
        return el.id != client.id;
      });
    },
    /*
      Create new user
    */
    async saveUser() {
      this.isSubmitting = true;

      let params = _.cloneDeep(this.user);

      // Remove name from client array
      params.clients = _.map(params.clients, client => _.omit(client, "name"));

      try {
        const result = await addUser(params);

        if (result && !result.iserror) {
          //this.resetUser();
          this.$refs.userModal.hide();
          //this.refreshTable();
          EventBus.$emit("triggerAlert", {
            type: "success",
            message: "User was added successfully"
          });
          this.resetForm(); // resets user and refreshes table
        } else {
          // there was an error
          switch (result.response.status) {
          case 409: {
            this.validationErrors = {
              email: "A user with this email address already exists",
              dontclear: true
            };
            EventBus.$emit("triggerAlert", {
              type: "danger",
              message: "A user with this email address already exists"
            });
            break;
          }
          case 422: {
            this.validationErrors = result.body.errors;
            this.validationErrors.dontclear = true;
            EventBus.$emit("triggerAlert", {
              type: "danger",
              message: "User could not be added"
            });
            break;
          }
          default: {
            EventBus.$emit("triggerAlert", {
              type: "danger",
              message: "An unknown error occurred"
            });
            break;
          }
          }
        }
      } catch (result) {
        EventBus.$emit("triggerAlert", {
          type: "danger",
          message: "An unknown error occurred"
        });
      }

      this.isSubmitting = false;
    },
    /*
      Update user
    */
    async updateUser() {
      this.isSubmitting = true;

      let params = _.cloneDeep(this.user);
      params = _.omit(params, ["id", "isDeleted", "email"]);
      // Remove name from client array
      params.clients = _.map(params.clients, client => _.omit(client, "name"));

      try {
        const result = await updateUser(this.user.id, params);
        //this.resetUser();
        this.$refs.userModal.hide();
        //this.refreshTable();
        EventBus.$emit("triggerAlert", {
          type: "success",
          message: "User was updated successfully"
        });
        this.resetForm(); // resets user and refreshes table
      } catch (result) {
        switch (result.status) {
        case 422: {
          this.validationErrors = result.body.errors;
          EventBus.$emit("triggerAlert", {
            type: "danger",
            message: "User could not be updated"
          });
          break;
        }
        default: {
          EventBus.$emit("triggerAlert", {
            type: "danger",
            message: "An unknown error occurred"
          });
          break;
        }
        }
      }

      this.isSubmitting = false;
    },
    /*
      Resend Welcome email to user
    */
    async resendEmail(id) {
      EventBus.$emit("showLoader");

      let params = { resendWelcomeEmail: true };

      try {
        const result = await updateUser(id, params);
        EventBus.$emit("triggerAlert", {
          type: "success",
          message: "Welcome email was sent successfully"
        });
      } catch (result) {
        switch (result.status) {
        case 422: {
          this.validationErrors = result.body.errors;
          EventBus.$emit("triggerAlert", {
            type: "danger",
            message: "Welcome email could not be sent"
          });
          break;
        }
        default: {
          EventBus.$emit("triggerAlert", {
            type: "danger",
            message: "An unknown error occurred"
          });
          break;
        }
        }
      }

      EventBus.$emit("hideLoader");
    },
    /*
      Log out and reset password
    */
    async resetPassword(id) {
      EventBus.$emit("showLoader");

      let params = { resetPassword: true };

      try {
        const result = await updateUser(id, params);
        EventBus.$emit("triggerAlert", {
          type: "success",
          message:
            "User logged out and reset password email was sent successfully"
        });
      } catch (result) {
        switch (result.status) {
        case 422: {
          this.validationErrors = result.body.errors;
          EventBus.$emit("triggerAlert", {
            type: "danger",
            message: "User could not be logged out."
          });
          break;
        }
        default: {
          EventBus.$emit("triggerAlert", {
            type: "danger",
            message: "An unknown error occurred"
          });
          break;
        }
        }
      }

      EventBus.$emit("hideLoader");
    },
    /*
      Delete/deactivate user
    */
    async deleteUser(id, name) {
      if (confirm(`Are you sure you want to deactivate ${name}?`)) {
        EventBus.$emit("showLoader");
        try {
          const result = await deleteUser(id);
          this.refreshTable();
          EventBus.$emit("triggerAlert", {
            type: "success",
            message: "User was deactivated successfully"
          });
        } catch (e) {
          EventBus.$emit("triggerAlert", {
            type: "danger",
            message: "User could not be deactivated at this time"
          });
        }
        EventBus.$emit("hideLoader");
      }
    },
    /*
      Helpers
    */
    addUser() {
      this.isEditing = false;
      this.$refs.userModal.show();
    },
    editUser(user) {
      this.user = user;
      this.isEditing = true;
      this.$refs.userModal.show();
    },
    hideModal() {
      this.resetForm();
      this.$refs.userModal.hide();
    },
    resetForm() {
      this.resetUser();
      this.refreshTable();
      this.isSubmitting = false;
      this.validationErrors = {};
      this.pristineEmail = true;
      this.pristineName = true;
    },
    resetUser() {
      this.user = {
        name: "",
        email: "",
        clients: [],
        useApi: false,
        roleId: 2
      };
    },
    inArray(obj, list) {
      return list.some(elem => elem.id === obj.id);
    }
  }
};
</script>
<style lang="scss">
@import "../assets/styles/main.scss";
.action-icon {
  display: inline-block;
  color: $ui-gray;
  & + .action-icon {
    margin-left: 10px;
  }
}
.user-clients {
  li {
    &::after {
      content: ",";
      position: relative;
    }
    &:last-child {
      &::after {
        content: none;
      }
    }
  }
}
.client-search {
  position: relative;
  .search-input {
    position: relative;
    padding: 5px;
    background: $ui-gray-lightest;
    border-radius: 3px;
    .icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 15px;
    }
    .clear-search {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 15px;
      cursor: pointer;
    }
    input {
      padding-left: 40px;
    }
  }
  .search-results {
    position: absolute;
    width: 100%;
    z-index: 999;
    padding: 15px;
    margin: 0;
    background: #fff;
    border: 1px solid $ui-gray-lighter;
    box-shadow: 1px 1px 15px 0 rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    max-height: 250px;
    overflow: auto;
  }
}
.selected-clients {
  margin-top: 20px;
  h4 {
    font-size: 1.2rem;
  }
}
.client-item {
  position: relative;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid $ui-gray-lighter;
  border-radius: $border-radius;
  box-shadow: 1px 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 200ms;
  p {
    margin: 0px;
    line-height: normal;
  }
  .icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.2rem;
    color: $ui-gray-light;
  }
  &.add-client {
    &:hover,
    &.selected {
      cursor: pointer;
      border-color: $ui-blue;
      box-shadow: 0px 0px 5px rgba($ui-blue, 0.5);
      .icon {
        color: $ui-blue;
      }
    }
    &.selected {
      opacity: 0.5;
    }
  }
  &.remove-client {
    &:hover {
      cursor: pointer;
      border-color: $danger;
      box-shadow: 0px 0px 5px rgba($danger, 0.5);
      .icon {
        color: $danger;
      }
    }
  }
  & + .client-item {
    margin-top: 5px;
  }
}
.smallclientidtext {
  font-size: small;
}
.scrollfix {
  overflow-x: hidden;
}
.required {
  color: $danger;
  margin-left: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/Users.vue